import { isEqual, noop } from 'lodash';
import { createContext, useContext, useMemo } from 'react';
import {
  type PerformanceReportFiltersFormValues,
  PerformanceReportFilterTransactionStatus,
} from '../types';

export type PerformanceReportContextType = {
  filters?: PerformanceReportFiltersFormValues;
  updateContext: (context: Partial<PerformanceReportContextType>) => void;
};

export const PERFORMANCE_REPORT_CONTEXT_DEFAULTS = {
  filters: {
    reportingPeriod: new Date().getFullYear(),
    agents: [],
    teams: [],
    status: [PerformanceReportFilterTransactionStatus.closed],
    transactionType: [],
    hideTerminatedAgents: false,
    leadSources: [],
  },
  updateContext: noop,
} satisfies PerformanceReportContextType;

export const PerformanceReportContext =
  createContext<PerformanceReportContextType>(
    PERFORMANCE_REPORT_CONTEXT_DEFAULTS,
  );

const defaults = PERFORMANCE_REPORT_CONTEXT_DEFAULTS.filters;

// Baseline used for badge counting — empty arrays for status
// so that the default "Closed" status chip registers as 1 active filter.
const filterCountBaseline = {
  ...defaults,
  status: [],
};

export function usePerformanceReportContext() {
  const context = useContext(PerformanceReportContext);

  const filterCount = useMemo(() => {
    const { filters } = context;
    if (!filters) {
      return 0;
    }

    return (
      Object.keys(filterCountBaseline) as Array<
        keyof typeof filterCountBaseline
      >
    ).filter((key) => !isEqual(filters[key], filterCountBaseline[key])).length;
  }, [context.filters]);

  return { ...context, filterCount };
}
